import { useEffect, useState } from "react"

/**
 * Fetches a Metabase public-question JSON endpoint.
 *
 * Metabase public shares (`/public/question/<uuid>.json`) are unauthenticated —
 * the UUID acts as the bearer secret. Only use for data safe to expose to
 * logged-out users (no row-level auth, no user scoping).
 */
export function useMetabasePublicQuestion<TRow>(url: string) {
	const [rows, setRows] = useState<TRow[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<Error | null>(null)

	useEffect(() => {
		const controller = new AbortController()
		setIsLoading(true)
		setError(null)

		void (async () => {
			try {
				const res = await fetch(url, { signal: controller.signal })
				if (!res.ok) {
					throw new Error(
						`Metabase request failed with status ${res.status}`,
					)
				}
				const payload = (await res.json()) as unknown
				if (!Array.isArray(payload)) {
					throw new Error("Metabase response was not an array")
				}
				setRows(payload as TRow[])
			} catch (err) {
				if ((err as Error).name === "AbortError") return
				console.error("Metabase public question fetch failed", err)
				setError(err as Error)
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false)
				}
			}
		})()

		return () => controller.abort()
	}, [url])

	return { rows, isLoading, error }
}
